import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AbbonamentiCliente from '../../components/admin/AbbonamentiCliente';
import PagamentiCliente from '../../components/admin/PagamentiCliente';
import api from '../../services/api';
import {
  ArrowLeft, Edit, Trash2, User, Mail, Phone, MapPin, 
  Calendar, CheckCircle, XCircle, AlertCircle, Users, Plus,
  Building, CreditCard, Ticket, Camera, School, Search, X, Key, AlertTriangle
} from 'lucide-react';
import { notify } from '../../utils/notifications';

const ClienteDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [tutori, setTutori] = useState([]);
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTutoreModal, setShowTutoreModal] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'info', 'scuola', 'note', etc.
  const [tempFormData, setTempFormData] = useState({});
  const [invioEmail, setInvioEmail] = useState(false);
  const [showAbbonamentoModal, setShowAbbonamentoModal] = useState(false);
  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [editingTutore, setEditingTutore] = useState(null);
  const [formData, setFormData] = useState({});
  const [tutoreFormData, setTutoreFormData] = useState({
    tipo: 'GENITORE',
    nome: '',
    cognome: '',
    codice_fiscale: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    note: ''
  });
  
  // Stati per ricerca scuole
  const [scuoleDisponibili, setScuoleDisponibili] = useState([]);
  const [searchScuola, setSearchScuola] = useState('');
  const [loadingScuole, setLoadingScuole] = useState(false);
  const [showScuoleModal, setShowScuoleModal] = useState(false);

  useEffect(() => {
    caricaDatiCliente();
  }, [id]);

  const caricaDatiCliente = async () => {
    try {
      setLoading(true);
      const [clienteRes, tutoriRes, abbonamentiRes, pagamentiRes] = await Promise.all([
        api.get(`/anagrafiche/titolari/${id}/`),
        api.get(`/anagrafiche/tutori/?tutore=${id}`),
        api.get(`/abbonamenti/abbonamenti/?titolare=${id}`),
        api.get(`/pagamenti/pagamenti/?titolare=${id}`)
      ]);

      setCliente(clienteRes.data);
      setFormData(clienteRes.data);
      setTutori(tutoriRes.data.results || tutoriRes.data);
      setAbbonamenti(abbonamentiRes.data.results || abbonamentiRes.data);
      setPagamenti(pagamentiRes.data.results || pagamentiRes.data);
    } catch (error) {
      console.error('Errore caricamento:', error);
      notify.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };
  
  const caricaScuoleGestite = async () => {
    try {
      setLoadingScuole(true);
      // Carica tutte le linee
      const response = await api.get('/trasporti/linee/');
      const linee = response.data.results || response.data;
      
      // Estrai tutte le scuole uniche dalle linee
      const scuoleSet = new Map();
      
      linee.forEach(linea => {
        try {
          if (linea.scuole_servite) {
            const scuoleStr = linea.scuole_servite.trim();
            if (scuoleStr.startsWith('[') || scuoleStr.startsWith('{')) {
              const scuole = JSON.parse(scuoleStr);
              if (Array.isArray(scuole)) {
                scuole.forEach(scuola => {
                  // Usa codice_meccanografico come chiave unica
                  if (scuola.codice_meccanografico && !scuoleSet.has(scuola.codice_meccanografico)) {
                    scuoleSet.set(scuola.codice_meccanografico, scuola);
                  }
                });
              }
            }
          }
        } catch (e) {
          console.error('Errore parsing scuole linea:', e);
        }
      });
      
      // Converti in array e ordina
      const scuoleArray = Array.from(scuoleSet.values()).sort((a, b) => 
        a.denominazione.localeCompare(b.denominazione)
      );
      
      setScuoleDisponibili(scuoleArray);
    } catch (error) {
      console.error('Errore caricamento scuole:', error);
      notify.error('Errore caricamento scuole');
    } finally {
      setLoadingScuole(false);
    }
  };

  const handleSelectScuola = (scuola) => {
    const scuolaNome = scuola.denominazione_scuola || scuola.denominazione || scuola.descrizione_scuola;
    
    if (editingField === 'info') {
      setTempFormData(prev => ({ ...prev, scuola: scuolaNome }));
    } else {
      setFormData(prev => ({ ...prev, scuola: scuolaNome }));
    }
    
    setShowScuoleModal(false);
    setSearchScuola('');
    notify.success('Scuola selezionata');
  };

  const handleStartEdit = (field) => {
    setEditingField(field);
    setTempFormData({ ...formData });
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempFormData({});
  };

  const handleSaveField = async (field) => {
    if (field === 'info' && (!tempFormData.nome?.trim() || !tempFormData.cognome?.trim())) {
      notify.error('Nome e cognome sono obbligatori');
      return;
    }
    try {
      // Prepara i dati da inviare (solo i campi modificabili)
      const dataToSend = {
        nome: tempFormData.nome,
        cognome: tempFormData.cognome,
        codice_fiscale: tempFormData.codice_fiscale || '',
        email: tempFormData.email,
        telefono: tempFormData.telefono,
        indirizzo: tempFormData.indirizzo,
        citta: tempFormData.citta,
        cap: tempFormData.cap,
        provincia: tempFormData.provincia,
        scuola: tempFormData.scuola || '',
        note: tempFormData.note || ''
      };
      
      console.log('Invio dati:', dataToSend);
      
      await api.patch(`/anagrafiche/titolari/${id}/`, dataToSend);
      setEditingField(null);
      // Si ricarica dal server: il salvataggio può aver toccato anche
      // l'account di accesso, ad esempio quando cambia l'email
      await caricaDatiCliente();
      notify.success('Aggiornato con successo');
    } catch (error) {
      console.error('Errore salvataggio:', error);
      console.error('Response data:', error.response?.data);
      const dati = error.response?.data || {};
      const primo = Object.values(dati).flat().find((v) => typeof v === 'string');
      notify.error(dati.detail || primo || ('Errore salvataggio: ' + error.message));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const toastId = notify.loading('Aggiornamento...');
    try {
      await api.put(`/anagrafiche/titolari/${id}/`, formData);
      await caricaDatiCliente();
      notify.update(toastId, { type: 'success', render: 'Aggiornato' });
    } catch (error) {
      notify.update(toastId, { type: 'error', render: 'Errore' });
    }
  };

  const handleReinviaEmail = async (rigeneraPassword) => {
    const conferma = rigeneraPassword
      ? 'Generare una NUOVA password e inviarla via email? La password attuale del cliente smetterà di funzionare.'
      : 'Inviare al cliente l\'email con i dati del suo account?';
    if (!window.confirm(conferma)) return;

    setInvioEmail(true);
    const toastId = notify.loading('Invio in corso...');
    try {
      const res = await api.post(`/anagrafiche/titolari/${id}/reinvia_email/`, {
        rigenera_password: rigeneraPassword ? 'true' : 'false',
      });
      notify.update(toastId, { type: 'success', render: res.data.messaggio });
      await caricaDatiCliente();
    } catch (error) {
      const dati = error.response?.data || {};
      const primo = Object.values(dati).flat().find((v) => typeof v === 'string');
      notify.update(toastId, {
        type: 'error',
        render: dati.errore || dati.detail || primo || 'Invio non riuscito',
      });
    } finally {
      setInvioEmail(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questo cliente?')) return;
    try {
      await api.delete(`/anagrafiche/titolari/${id}/`);
      notify.success('Eliminato');
      navigate('/admin/clienti');
    } catch (error) {
      notify.error('Errore eliminazione');
    }
  };

  const handleOpenTutoreModal = (tutore = null) => {
    if (tutore) {
      setEditingTutore(tutore);
      setTutoreFormData(tutore);
    } else {
      setEditingTutore(null);
      setTutoreFormData({
        titolare: parseInt(id),
        tipo: 'GENITORE',
        nome: '',
        cognome: '',
        codice_fiscale: '',
        telefono: '',
        email: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        note: ''
      });
    }
    setShowTutoreModal(true);
  };

  const handleSaveTutore = async (e) => {
    e.preventDefault();
    const toastId = notify.loading('Salvataggio...');
    try {
      if (editingTutore) {
        await api.put(`/anagrafiche/tutori/${editingTutore.id}/`, tutoreFormData);
      } else {
        await api.post('/anagrafiche/tutori/', { ...tutoreFormData, titolare: parseInt(id) });
      }
      notify.update(toastId, { type: 'success', render: 'Salvato' });
      await caricaDatiCliente();
      setShowTutoreModal(false);
    } catch (error) {
      notify.update(toastId, { type: 'error', render: 'Errore' });
    }
  };

  const handleDeleteTutore = async (tutoreId) => {
    if (!window.confirm('Eliminare questo tutore?')) return;
    try {
      await api.delete(`/anagrafiche/tutori/${tutoreId}/`);
      notify.success('Eliminato');
      await caricaDatiCliente();
    } catch (error) {
      notify.error('Errore');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-[#1b94ce]"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!cliente) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600">Cliente non trovato</p>
          <button onClick={() => navigate('/admin/clienti')} className="mt-3 text-[#1b94ce] text-sm hover:underline">
            Torna alla lista
          </button>
        </div>
      </AdminLayout>
    );
  }

  const abbonamentiAttivi = abbonamenti.filter(a => a.stato === 'ATTIVO').length;
  const totaleSpeso = pagamenti.reduce((acc, p) => acc + parseFloat(p.importo || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/clienti')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Clienti</span>
          </button>
        </div>

        {/* Con l'anagrafica incompleta il tesserino non passa a bordo:
            va detto qui, non lasciato scoprire all'autista */}
        {cliente.dati_mancanti?.length > 0 && (
          <div className="mb-6 px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                Anagrafica incompleta — l'abbonamento non è valido a bordo
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Dati da compilare: <b>{cliente.dati_mancanti.join(', ').toLowerCase()}</b>
              </p>
            </div>
          </div>
        )}

        {/* Header Cliente */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Foto Profilo o Avatar */}
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {cliente.foto ? (
                  <img 
                    src={cliente.foto} 
                    alt={`${cliente.nome} ${cliente.cognome}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                  {cliente.nome} {cliente.cognome}
                </h1>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className={cliente.codice_fiscale ? 'font-mono' : 'text-amber-700 font-medium'}>
                    {cliente.codice_fiscale || 'codice fiscale non indicato'}
                  </span>
                  <span className="text-gray-300">•</span>
                  <span>Username: {cliente.utente_username}</span>
                  {cliente.consenso_privacy && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Privacy OK
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleReinviaEmail(false)}
                disabled={invioEmail || !cliente?.email}
                title={cliente?.email ? 'Invia al cliente i dati del suo account' : 'Il cliente non ha un indirizzo email'}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-40"
              >
                <Mail className="w-4 h-4" />
                Reinvia email
              </button>
              <button
                onClick={() => handleReinviaEmail(true)}
                disabled={invioEmail || !cliente?.email}
                title="Genera una nuova password e la invia via email"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-40"
              >
                <Key className="w-4 h-4" />
                Nuova password
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>

        {/* Layout 2 Colonne */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonna Principale (70%) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informazioni Anagrafiche */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Informazioni</h2>
                {editingField !== 'info' ? (
                  <button
                    onClick={() => handleStartEdit('info')}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:border-[#1b94ce] hover:text-[#1b94ce] transition"
                  >
                    <Edit className="w-4 h-4" />
                    Modifica dati
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={() => handleSaveField('info')}
                      className="text-sm text-[#1b94ce] hover:text-[#157bb0] font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Salva
                    </button>
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-6">
                {editingField === 'info' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nome *</label>
                      <input
                        type="text"
                        required
                        value={tempFormData.nome || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, nome: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cognome *</label>
                      <input
                        type="text"
                        required
                        value={tempFormData.cognome || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, cognome: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Codice fiscale</label>
                      <input
                        type="text"
                        value={tempFormData.codice_fiscale || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, codice_fiscale: e.target.value.toUpperCase() })}
                        maxLength={16}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm font-mono uppercase"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                      <input
                        type="email"
                        value={tempFormData.email || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, email: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Telefono</label>
                      <input
                        type="tel"
                        value={tempFormData.telefono || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, telefono: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Indirizzo</label>
                      <input
                        type="text"
                        value={tempFormData.indirizzo || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, indirizzo: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                        placeholder="Via, numero civico"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Città</label>
                      <input
                        type="text"
                        value={tempFormData.citta || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, citta: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">CAP</label>
                      <input
                        type="text"
                        value={tempFormData.cap || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, cap: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Provincia</label>
                      <input
                        type="text"
                        value={tempFormData.provincia || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, provincia: e.target.value.toUpperCase() })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                        maxLength={2}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scuola</label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="text"
                          value={tempFormData.scuola || ''}
                          onChange={(e) => setTempFormData({ ...tempFormData, scuola: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                          placeholder="Nome scuola"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            caricaScuoleGestite();
                            setShowScuoleModal(true);
                          }}
                          className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                          title="Seleziona scuola gestita"
                        >
                          <School className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Note</label>
                      <textarea
                        value={tempFormData.note || ''}
                        onChange={(e) => setTempFormData({ ...tempFormData, note: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent text-sm"
                        rows={3}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                      <div className="mt-1 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <a href={`mailto:${cliente.email}`} className="text-sm text-[#1b94ce] hover:underline">
                          {cliente.email}
                        </a>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Telefono</label>
                      <div className="mt-1 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <a href={`tel:${cliente.telefono}`} className="text-sm text-[#1b94ce] hover:underline">
                          {cliente.telefono}
                        </a>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Indirizzo</label>
                      <div className="mt-1 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {/* Comporre con una virgola fissa nascondeva la via
                              mancante: la riga sembrava piena quando non lo era */}
                          {[
                            cliente.indirizzo,
                            [cliente.cap, cliente.citta].filter(Boolean).join(' '),
                            cliente.provincia ? `(${cliente.provincia})` : '',
                          ].filter((p) => (p || '').trim()).join(', ') || '—'}
                          {!cliente.indirizzo && (
                            <span className="ml-2 text-xs font-medium text-amber-700">
                              via non indicata
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    {cliente.scuola && (
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scuola</label>
                        <div className="mt-1 flex items-center gap-2">
                          <School className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{cliente.scuola}</span>
                        </div>
                      </div>
                    )}
                    {cliente.note && (
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Note</label>
                        <p className="mt-1 text-sm text-gray-700">{cliente.note}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Tutori */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-gray-900">Tutori</h2>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    {tutori.length}
                  </span>
                </div>
                <button
                  onClick={() => handleOpenTutoreModal()}
                  className="text-sm text-[#1b94ce] hover:text-[#157bb0] font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi
                </button>
              </div>
              <div className="p-4 sm:p-6">
                {tutori.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">Nessun tutore associato</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tutori.map((tut) => (
                      <div key={tut.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900">{tut.nome} {tut.cognome}</p>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {tut.tipo_display || tut.tipo}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {tut.telefono && (
                                <p className="text-xs text-gray-600 flex items-center gap-1.5">
                                  <Phone className="w-3 h-3" />
                                  {tut.telefono}
                                </p>
                              )}
                              {tut.email && (
                                <p className="text-xs text-gray-600 flex items-center gap-1.5">
                                  <Mail className="w-3 h-3" />
                                  {tut.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleOpenTutoreModal(tut)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTutore(tut.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Abbonamento */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Abbonamento</h2>
                {abbonamenti.length === 0 && (
                  <button
                    onClick={() => setShowAbbonamentoModal(true)}
                    className="text-sm text-[#1b94ce] hover:text-[#157bb0] font-medium flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Crea
                  </button>
                )}
              </div>
              <div className="p-4 sm:p-6">
                <AbbonamentiCliente 
                  clienteId={id} 
                  abbonamenti={abbonamenti}
                  onUpdate={caricaDatiCliente}
                  showModal={showAbbonamentoModal}
                  setShowModal={setShowAbbonamentoModal}
                />
              </div>
            </div>

            {/* Pagamenti */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-gray-900">Pagamenti</h2>
                  {(() => {
                    // Residuo complessivo sulle mensilità ancora aperte
                    const residuo = (abbonamenti || [])
                      .flatMap((a) => a.rate || [])
                      .filter((r) => !r.pagata && !r.gratuita)
                      .reduce((acc, r) => acc + Number(r.importo), 0);
                    return residuo > 0 ? (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                        €{residuo.toFixed(2)} da incassare
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                        {pagamenti.length}
                      </span>
                    );
                  })()}
                </div>
                <button
                  onClick={() => setShowPagamentoModal(true)}
                  className="text-sm text-[#1b94ce] hover:text-[#157bb0] font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Registra pagamento
                </button>
              </div>
              <div className="p-4 sm:p-6">
                <PagamentiCliente 
                  clienteId={id} 
                  pagamenti={pagamenti}
                  abbonamenti={abbonamenti}
                  onUpdate={caricaDatiCliente}
                  showModal={showPagamentoModal}
                  setShowModal={setShowPagamentoModal}
                />
              </div>
            </div>
          </div>

          {/* Sidebar Destra (30%) */}
          <div className="space-y-6">
            {/* Foto Profilo */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Foto Profilo</h3>
              <div className="flex flex-col items-center">
                <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden mb-3">
                  {cliente.foto ? (
                    <img 
                      src={cliente.foto} 
                      alt={`${cliente.nome} ${cliente.cognome}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      <Camera className="w-12 h-12 mb-2" />
                      <p className="text-xs text-center">Nessuna foto<br/>caricata</p>
                    </div>
                  )}
                </div>
                {cliente.foto && (
                  <p className="text-xs text-gray-500 text-center">
                    Foto per identificazione a bordo
                  </p>
                )}
              </div>
            </div>

            {/* Statistiche */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Riepilogo</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#1b94ce]" />
                    </div>
                    <span className="text-sm text-gray-600">Tutori</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">{tutori.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                      <Ticket className="w-4 h-4 text-[#e62494]" />
                    </div>
                    <span className="text-sm text-gray-600">Abbonamento</span>
                  </div>
                  <span className={`text-lg font-semibold ${abbonamenti.length > 0 && abbonamentiAttivi > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {abbonamenti.length > 0 ? (abbonamentiAttivi > 0 ? 'ATTIVO' : 'NON ATTIVO') : 'NESSUNO'}
                  </span>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-gray-600" />
                      </div>
                      <span className="text-sm text-gray-600">Totale</span>
                    </div>
                    <span className="text-lg font-semibold text-gray-900">€{totaleSpeso.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Sistema */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Dettagli Sistema</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Registrato</label>
                  <div className="mt-1 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {new Date(cliente.data_creazione).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ultima Modifica</label>
                  <div className="mt-1 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {new Date(cliente.data_modifica).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Modifica Cliente */}

      {/* Modal Tutore */}
      {showTutoreModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTutore ? 'Modifica Tutore' : 'Nuovo Tutore'}
              </h2>
              <button onClick={() => setShowTutoreModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSaveTutore} className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Tutore</label>
                  <select
                    value={tutoreFormData.tipo}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    required
                  >
                    <option value="GENITORE">Genitore</option>
                    <option value="NONNO">Nonno/Nonna</option>
                    <option value="ZIO">Zio/Zia</option>
                    <option value="TUTORE_LEGALE">Tutore Legale</option>
                    <option value="ALTRO">Altro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={tutoreFormData.nome}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={tutoreFormData.cognome}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, cognome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                  <input
                    type="text"
                    value={tutoreFormData.codice_fiscale}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, codice_fiscale: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    maxLength={16}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={tutoreFormData.telefono}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, telefono: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={tutoreFormData.email}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
                  <input
                    type="text"
                    value={tutoreFormData.indirizzo}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, indirizzo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                  <input
                    type="text"
                    value={tutoreFormData.citta}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, citta: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                  <input
                    type="text"
                    value={tutoreFormData.cap}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, cap: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    maxLength={5}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                  <input
                    type="text"
                    value={tutoreFormData.provincia}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, provincia: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                    maxLength={2}
                    placeholder="RM"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    value={tutoreFormData.note}
                    onChange={(e) => setTutoreFormData({ ...tutoreFormData, note: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowTutoreModal(false)}
                  className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-[#1b94ce] text-white rounded-lg hover:bg-[#157bb0]"
                >
                  {editingTutore ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal Selezione Scuola */}
      {showScuoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <School className="w-5 h-5 text-indigo-600" />
                Seleziona Scuola ({scuoleDisponibili.length} scuole gestite)
              </h2>
              <button onClick={() => {
                setShowScuoleModal(false);
                setSearchScuola('');
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {/* Barra di ricerca */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca per nome o comune..."
                    value={searchScuola}
                    onChange={(e) => setSearchScuola(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {searchScuola && (
                    <button
                      onClick={() => setSearchScuola('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Lista scuole */}
              {loadingScuole ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  <p className="ml-3 text-gray-600">Caricamento...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const scuoleFiltrate = scuoleDisponibili.filter(scuola => {
                      if (!searchScuola) return true;
                      const search = searchScuola.toLowerCase();
                      return (
                        scuola.denominazione.toLowerCase().includes(search) ||
                        scuola.comune?.toLowerCase().includes(search) ||
                        scuola.codice_meccanografico?.toLowerCase().includes(search)
                      );
                    });

                    if (scuoleFiltrate.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <School className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600">
                            {searchScuola ? `Nessuna scuola trovata per "${searchScuola}"` : 'Nessuna scuola disponibile'}
                          </p>
                        </div>
                      );
                    }

                    return scuoleFiltrate.map((scuola) => (
                      <button
                        key={scuola.codice_meccanografico}
                        onClick={() => handleSelectScuola(scuola)}
                        className="w-full border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:bg-indigo-50 transition text-left"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{scuola.denominazione}</h3>
                            <p className="text-sm text-gray-600">
                              {scuola.codice_meccanografico}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {scuola.indirizzo && `${scuola.indirizzo}, `}
                              {scuola.comune} ({scuola.provincia})
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full font-semibold whitespace-nowrap ml-2">
                            {scuola.tipo_grado_istruzione}
                          </span>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowScuoleModal(false);
                  setSearchScuola('');
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ClienteDettaglio;
